import React, { useEffect, useMemo } from "react";
import classnames from "classnames";
import {
  useTable,
  // useAsyncDebounce,
  usePagination,
  useFlexLayout,
  useResizeColumns,
  useExpanded,
  useSortBy,
  useFilters,
} from "react-table";
import { filterCaseInsensitive } from "../../js/react-table-functions";

function DefaultColumnFilter({ column: { filterValue, preFilteredRows, setFilter } }) {
  const count = preFilteredRows.length;

  return (
    <input
      value={filterValue || ""}
      onChange={(e) => {
        setFilter(e.target.value || undefined); // Set undefined to remove the filter entirely
      }}
      placeholder={`Search ${count} records...`}
    />
  );
}

// credit to https://gist.github.com/Sahero/7f88df3e5a6d499ef196314384646e24 for the base of this component, which is designed to emulate the react-table v6 styling.
export default function ReactTable(props) {
  const {
    className = "",
    data,
    columns,
    loading = false,
    getTdProps = () => { },
    trClassName = "",
    SubComponent,
    defaultPageSize,
    pageSizeOptions,
    pageCount = -1,
    autoResetPage = true,
    manualPagination = false,
    defaultFiltered = [{ id: "", value: "" }],
    defaultSorted = [{ id: "", desc: false }],
    showPageSizeOptions = true,
    onFetchData = () => { },
  } = props;

  /* Defining default column width for every column */
  const defaultColumn = React.useMemo(
    () => ({
      minWidth: 30,
      width: 50,
      maxWidth: 400,
      Filter: DefaultColumnFilter,
      // filter: defaultFilterMethod,
    }),
    [],
  );

  /* Define custom filter types for specific sorting */
  const filterTypes = useMemo(
    () => ({
      filterCaseInsensitive: filterCaseInsensitive,
    }),
    [],
  );

  /* If the table is expandable then add the expander column */
  if (SubComponent) {
    columns.unshift({
      Header: "",
      id: "expander",
      resizable: false,
      className: "text-center",
      Cell: ({ row }) => {
        return (
          // Applying the toggle expander props i.e onClick, style and title
          <div
            {...row.getToggleRowExpandedProps()}
            title="Click here to see more information"
            className="rt-td rt-expandable p-0"
          >
            <div className={classnames("rt-expander", row.isExpanded ? "-open" : "")}>•</div>
          </div>
        );
      },
    });
  }

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      SubComponent,
      filterTypes,
      pageCount: pageCount,
      manualPagination: manualPagination,
      autoResetPage: autoResetPage,
      initialState: {
        pageIndex: 0,
        pageSize: defaultPageSize,
        filters: defaultFiltered,
        sortBy: defaultSorted,
        // defaultFilterMethod: defaultFilterMethod
      },
    },
    useFlexLayout,
    useResizeColumns,
    useFilters,
    useSortBy,
    useExpanded,
    usePagination,
  );

  // FIXME: The useAsyncDebounce method causes babel to error out on builds
  // Only call data fetch function when pageIndex changes (for search bar)
  useEffect(() => {
    onFetchData({ pageIndex });
  }, [pageIndex]);

  return (
    // Applying the table props
    <div className={classnames(className, "ReactTable")}>
      <div {...getTableProps} className="rt-table">
        {/* Sortable Header Row */}
        <div className="rt-thead -header">
          {
            // Looping over the header rows
            headerGroups.map((headerGroup, i) => (
              //Applying the header row props
              <div
                // FIXME: key..
                key={i}
                {...headerGroup.getHeaderGroupProps()}
                className="rt-tr"
              >
                {
                  // Looping over the headers in each row
                  headerGroup.headers.map((column) => {
                    /*Checking if column can be resized and applying resizable props along with
                    header cell props and finally rendering the header cell
                    */
                    return column.resizable === false ? (
                      <>
                        <div
                          {...column.getHeaderProps(column.getSortByToggleProps())}
                          className={classnames(
                            "rt-th",
                            column.isSorted ? (column.isSortedDesc ? "-sort-desc" : "-sort-asc") : null,
                          )}
                        >
                          {column.render("Header")}
                        </div>
                      </>
                    ) : (
                      <div
                        {...column.getHeaderProps(column.getSortByToggleProps())}
                        className={classnames(
                          "rt-th",
                          "tr-thead",
                          "rt-resizable-header",
                          column.isSorted ? (column.isSortedDesc ? "-sort-desc" : "-sort-asc") : null,
                        )}
                      >
                        <div className="rt-resizable-header-content">{column.render("Header")}</div>
                        <div {...column.getResizerProps()} className="rt-resizer" />
                      </div>
                    );
                  })
                }
              </div>
            ))
          }
        </div>

        {/* Filter Row */}
        <div className="rt-thead -filters">
          {
            // Looping over the header rows
            headerGroups.map((headerGroup, i) => (
              //Applying the header row props
              <div
                // FIXME: key..
                key={i}
                {...headerGroup.getHeaderGroupProps()}
                className="rt-tr"
              >
                {
                  //Looping over the headers in each row
                  headerGroup.headers.map((column) => {
                    /*Checking if column can be resized and applying resizable props along with
                    header cell props and finally rendering the header cell
                    */
                    return column.disableFilters ? null : column.resizable === false ? (
                      <>
                        <div {...column.getHeaderProps()} className={classnames("rt-th")}>
                          {column.render("Filter")}
                        </div>
                      </>
                    ) : (
                      <div {...column.getHeaderProps()} className="rt-th rt-resizable-header">
                        <div className="rt-resizable-header-content">{column.render("Filter")}</div>
                        <div {...column.getResizerProps()} className="rt-resizer" />
                      </div>
                    );
                  })
                }
              </div>
            ))
          }
        </div>

        <div className="rt-tbody">
          {/* Applying the table body props*/}
          <div {...getTableBodyProps()} className="rt-tr-group">
            {
              // Looping over the table rows
              page.map((row, ind) => {
                // Preparing row for rendering
                prepareRow(row);
                return (
                  <React.Fragment key={row.id}>
                    {/* Applying the row props and adding classname for alternate rows*/}
                    <div
                      {...row.getRowProps()}
                      className={classnames("rt-tr", ind % 2 === 0 ? "-odd" : "-even", trClassName)}
                    >
                      {
                        // Looping over the row cells
                        row.cells.map((cell) => {
                          return (
                            // Applying the cell props and rendering the cell content
                            <div
                              // FIXME
                              key={cell}
                              {...getTdProps(cell)}
                              {...cell.getCellProps()}
                              className={classnames("rt-td", cell.column.className)}
                            >
                              {cell.render("Cell")}
                            </div>
                          );
                        })
                      }
                    </div>
                    {/* Checking row expansion */}
                    {row.isExpanded ? SubComponent({ row }) : null}
                  </React.Fragment>
                );
              })
            }
          </div>
        </div>
      </div>
      <div className="pagination-bottom">
        <div className="-pagination">
          <div className="-previous">
            <button className="-btn" onClick={() => previousPage()} disabled={!canPreviousPage}>
              {"Previous"}
            </button>
          </div>
          <div className="-center">
            <span className="-pageInfo">
              Page{" "}
              <div className="-pageJump">
                <input
                  type="number"
                  // defaultValue={pageIndex + 1}
                  value={pageIndex + 1}
                  onChange={(e) => {
                    const page = e.target.value ? Number(e.target.value) - 1 : 0;
                    gotoPage(page);
                  }}
                />{" "}
                of <span className="-totalPages">{pageOptions.length}</span>
              </div>
            </span>
            {showPageSizeOptions && (
              <span className="select-wrap">
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                  }}
                >
                  {pageSizeOptions.map((pageSize) => (
                    <option key={pageSize} value={pageSize}>
                      {pageSize} rows
                    </option>
                  ))}
                </select>
              </span>
            )}
          </div>
          <div className="-next">
            <button className="-btn" onClick={() => nextPage()} disabled={!canNextPage}>
              {"Next"}
            </button>{" "}
          </div>
        </div>
      </div>
      {/*Loading overlay*/}
      <div className={classnames("-loading", loading ? "-active" : "")}>
        <div className="-loading-inner">Searching for results...</div>
      </div>
    </div>
  );
}
